"use client";

import { Container } from "@/components/ui/Container";
import { Reveal } from "@/components/ui/Reveal";
import { SectionLabel } from "@/components/ui/SectionLabel";
import { SplitHeading } from "@/components/ui/SplitHeading";
import { SECTION_TITLE_CLASS } from "@/lib/typography";
import type { ProcessStep } from "@/lib/content/types";

type ProcessProps = {
  process: ProcessStep[];
  label?: string;
  title?: string;
  description?: string;
};

export function Process({
  process,
  label,
  title,
  description,
}: ProcessProps) {
  if (!process.length || !title) return null;

  return (
    <section id="process" className="py-20 md:py-28 lg:py-32">
      <Container>
        <div className="grid gap-12 lg:grid-cols-12 lg:items-start lg:gap-16 xl:gap-20">
          <div className="lg:sticky lg:top-28 lg:col-span-4 lg:self-start">
            <Reveal>
              {label ? <SectionLabel className="mb-5">{label}</SectionLabel> : null}
              <SplitHeading className={SECTION_TITLE_CLASS}>
                {title}
              </SplitHeading>
              {description ? (
                <p className="mt-6 max-w-sm text-base leading-relaxed text-muted md:text-lg">
                  {description}
                </p>
              ) : null}
            </Reveal>
          </div>

          <ol className="lg:col-span-8">
            {process.map((step, index) => (
              <Reveal
                as="li"
                key={step.title}
                delay={index * 0.05}
                className="grid grid-cols-[3rem_1fr] gap-4 border-t border-white/[0.08] py-7 md:grid-cols-[4rem_1fr] md:gap-8 md:py-8 last:border-b last:border-white/[0.08]"
              >
                <span className="pt-1 font-mono text-xs text-muted/60">
                  {step.number}
                </span>
                <div>
                  <h3 className="text-xl font-medium tracking-tight md:text-2xl">
                    {step.title}
                  </h3>
                  <p className="mt-2 max-w-xl text-sm leading-relaxed text-muted md:text-[0.9375rem]">
                    {step.description}
                  </p>
                </div>
              </Reveal>
            ))}
          </ol>
        </div>
      </Container>
    </section>
  );
}
